/**
 * Emergency contacts — trusted adults notified on SOS / safety alerts.
 * Sorted by priority (1 = call first). Phone numbers must be E.164.
 */
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2, Phone, Mail, PhoneCall } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  emergencyApi,
  type EmergencyContact,
  type EmergencyContactCreate,
  type Relationship,
} from '@/api/emergency';
import { PageHeader } from './PageHeader';

const RELATIONSHIPS: { value: Relationship; label: string }[] = [
  { value: 'parent', label: 'Parent' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'aunt_uncle', label: 'Aunt / Uncle' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'friend', label: 'Friend' },
  { value: 'neighbour', label: 'Neighbour' },
  { value: 'other', label: 'Other' },
];

const ContactSchema = z.object({
  name: z.string().trim().min(1).max(120),
  relationship: z.enum([
    'parent',
    'guardian',
    'grandparent',
    'aunt_uncle',
    'sibling',
    'friend',
    'neighbour',
    'other',
  ]),
  phone: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{1,14}$/, 'Must be E.164, e.g. +15551234567'),
  email: z
    .string()
    .trim()
    .email('Invalid email')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  priority: z.number().int().min(1).max(100),
  notify_on_sos: z.boolean(),
  notify_on_battery_low: z.boolean(),
  notify_on_geofence_exit: z.boolean(),
});
type ContactValues = z.infer<typeof ContactSchema>;

export function EmergencyPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const contacts = useQuery({
    queryKey: ['emergency-contacts'],
    queryFn: () => emergencyApi.list(),
  });

  const create = useMutation({
    mutationFn: (input: EmergencyContactCreate) => emergencyApi.create(input),
    onSuccess: () => {
      toast.success('Contact added.');
      void queryClient.invalidateQueries({ queryKey: ['emergency-contacts'] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Add failed.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => emergencyApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['emergency-contacts'] }),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ContactValues>({
    resolver: zodResolver(ContactSchema),
    defaultValues: {
      name: '',
      relationship: 'parent',
      phone: '',
      email: '',
      priority: 10,
      notify_on_sos: true,
      notify_on_battery_low: false,
      notify_on_geofence_exit: false,
    },
  });

  const onSubmit = async (v: ContactValues) => {
    await create.mutateAsync(v as EmergencyContactCreate);
    reset();
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.emergency.title')}
        icon={PhoneCall}
        tint="green"
        description={t('pages.emergency.description')}
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_24rem]">
        <section className="space-y-3">
          {contacts.isPending && (
            <Card>
              <CardContent className="space-y-3 p-6">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          )}
          {contacts.data?.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-sm text-[color:var(--kz-color-ink-secondary)]">
                No emergency contacts yet. Add one on the right — start with a parent or grandparent.
              </CardContent>
            </Card>
          )}
          {(contacts.data ?? []).map((c) => (
            <ContactCard key={c.id} contact={c} onRemove={() => remove.mutate(c.id)} />
          ))}
        </section>

        <aside>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Add a contact</CardTitle>
              <CardDescription>Phone must be E.164 (start with `+`).</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-3">
                <Field label="Name" error={errors.name?.message}>
                  <Input placeholder="Grandma Anna" {...register('name')} />
                </Field>
                <Field label="Relationship">
                  <select
                    className="flex h-9 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent px-3 text-sm"
                    {...register('relationship')}
                  >
                    {RELATIONSHIPS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Phone" error={errors.phone?.message}>
                  <Input placeholder="+15551234567" {...register('phone')} />
                </Field>
                <Field label="Email (optional)" error={errors.email?.message}>
                  <Input type="email" placeholder="grandma@example.com" {...register('email')} />
                </Field>
                <Field label="Priority" error={errors.priority?.message}>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    {...register('priority', { valueAsNumber: true })}
                  />
                </Field>
                <NotifyToggle
                  label="Notify on SOS"
                  checked={watch('notify_on_sos')}
                  onChange={(v) => setValue('notify_on_sos', v)}
                />
                <NotifyToggle
                  label="Notify on low battery"
                  checked={watch('notify_on_battery_low')}
                  onChange={(v) => setValue('notify_on_battery_low', v)}
                />
                <NotifyToggle
                  label="Notify on geofence exit"
                  checked={watch('notify_on_geofence_exit')}
                  onChange={(v) => setValue('notify_on_geofence_exit', v)}
                />
                <Button type="submit" disabled={create.isPending} className="mt-1">
                  {create.isPending ? 'Adding…' : 'Add contact'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
      {error && (
        <p className="text-xs text-[color:var(--kz-color-status-danger)]">{error}</p>
      )}
    </div>
  );
}

function NotifyToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-md border border-[color:var(--kz-color-border-subtle)] px-3 py-2 text-sm">
      <span>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function ContactCard({
  contact,
  onRemove,
}: {
  contact: EmergencyContact;
  onRemove: () => void;
}) {
  const triggers: string[] = [];
  if (contact.notify_on_sos) triggers.push('SOS');
  if (contact.notify_on_battery_low) triggers.push('low battery');
  if (contact.notify_on_geofence_exit) triggers.push('geofence exit');
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[color:var(--kz-color-brand-primary)]/10 text-sm font-semibold text-[color:var(--kz-color-brand-primary)]">
          {contact.priority}
        </span>
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-medium">
            {contact.name}{' '}
            <span className="text-xs font-normal text-[color:var(--kz-color-ink-muted)]">
              · {contact.relationship.replace('_', ' / ')}
            </span>
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[color:var(--kz-color-ink-secondary)]">
            <span className="inline-flex items-center gap-1 font-mono">
              <Phone className="size-3" /> {contact.phone}
            </span>
            {contact.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="size-3" /> {contact.email}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--kz-color-ink-muted)]">
            Notify on: {triggers.length ? triggers.join(', ') : 'nothing'}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Remove">
          <Trash2 className="size-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
